import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllCompaniesForGlobalSelection, getUserCompaniesAndRoles } from '@/modules/auth/redux/AuthCRUD';
import * as auth from '@/modules/auth/redux/AuthRedux';
import { applyUserCompaniesAndRoles } from '@/__core/permissions/utils/applyUserCompaniesAndRoles';
import type { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import type { CompanyScopeOption } from '@/utils/companyAutocompleteScope';
import { RootState } from '@/setup';
import {
  resolveModuleAccessibleCompanies,
  type ModuleCompanyScopeKey,
  type ModuleAccessibleCompaniesResult,
} from '../utils/resolveModuleAccessibleCompanies';
import type { CompanyPermission } from '../utils/PermissionManager';

export interface ToolbarCompanyOption {
  id: string;
  name: string;
}

export interface UseModuleAccessibleCompaniesResult extends ModuleAccessibleCompaniesResult {
  companyOptions: ToolbarCompanyOption[];
}

const COMPANIES_FETCH_PROMISE_KEY = '__moduleAccessibleCompaniesFetchPromise';

const mapFallbackFromApi = (
  list: Array<{ id?: string; name?: string; company_id?: string; company_name?: string }>
): CompanyScopeOption[] => {
  const mapped: CompanyScopeOption[] = [];
  list.forEach((item) => {
    const companyId = (item.company_id || item.id || '').trim();
    if (!companyId) return;
    mapped.push({
      company_id: companyId,
      company_name: (item.company_name || item.name || companyId).trim(),
      company_type: 'company',
    });
  });
  return mapped;
};

/**
 * Redux-backed company scope for module list toolbars (Users, Roles first; reusable for other modules).
 * Loads auth.companies from API when Redux list is empty (companies are not redux-persisted).
 */
export function useModuleAccessibleCompanies(
  moduleKey: ModuleCompanyScopeKey
): UseModuleAccessibleCompaniesResult {
  const dispatch = useDispatch();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const companies = useSelector((state: RootState) => state.auth?.companies ?? []);
  const globalRoles = useSelector((state: RootState) => state.auth?.globalRoles ?? []);
  const selectedCompany = useSelector(
    (state: RootState) => state.auth?.selectedCompany ?? null
  );
  const hasGlobalRoleFlag = useSelector((state: RootState) => state.auth?.hasGlobalRole ?? false);

  const [allCompaniesFallback, setAllCompaniesFallback] = useState<CompanyScopeOption[]>([]);
  const [loadedCompanies, setLoadedCompanies] = useState<CompanyData[]>([]);

  useEffect(() => {
    if (!accessToken || companies.length > 0) {
      setLoadedCompanies([]);
      return;
    }

    let cancelled = false;

    const runFetch = async () => {
      const existing = (window as typeof globalThis & {
        [COMPANIES_FETCH_PROMISE_KEY]?: Promise<{ normalized: CompanyData[]; hasGlobalRole: boolean }>;
      })[COMPANIES_FETCH_PROMISE_KEY];

      const promise =
        existing ||
        getUserCompaniesAndRoles().then((response) => {
          const parsed = applyUserCompaniesAndRoles(dispatch, response.data, {
            syncSelectedCompany: false,
          });
          return { normalized: parsed.companies, hasGlobalRole: parsed.hasGlobalRole };
        });

      if (!existing) {
        (window as typeof globalThis & {
          [COMPANIES_FETCH_PROMISE_KEY]?: Promise<{ normalized: CompanyData[]; hasGlobalRole: boolean }>;
        })[COMPANIES_FETCH_PROMISE_KEY] = promise;
      }

      try {
        const { normalized } = await promise;
        if (cancelled) return;
        setLoadedCompanies(normalized);
      } catch (error) {
        console.error('Failed to load user companies for module toolbar:', error);
        if (!cancelled) setLoadedCompanies([]);
      } finally {
        const win = window as typeof globalThis & {
          [COMPANIES_FETCH_PROMISE_KEY]?: Promise<{ normalized: CompanyData[]; hasGlobalRole: boolean }>;
        };
        if (win[COMPANIES_FETCH_PROMISE_KEY] === promise) {
          delete win[COMPANIES_FETCH_PROMISE_KEY];
        }
      }
    };

    runFetch();

    return () => {
      cancelled = true;
    };
  }, [accessToken, companies.length, dispatch]);

  const effectiveCompanies = companies.length > 0 ? companies : loadedCompanies;

  const hasGlobalRole = useMemo(
    () =>
      resolveModuleAccessibleCompanies({
        moduleKey,
        companies: effectiveCompanies,
        globalRoles,
        selectedCompany,
        hasGlobalRole: hasGlobalRoleFlag,
      }).hasGlobalRole,
    [moduleKey, effectiveCompanies, globalRoles, selectedCompany, hasGlobalRoleFlag]
  );

  useEffect(() => {
    if (!hasGlobalRole || effectiveCompanies.length > 0) {
      setAllCompaniesFallback([]);
      return;
    }

    let cancelled = false;

    getAllCompaniesForGlobalSelection()
      .then((list) => {
        if (cancelled) return;
        setAllCompaniesFallback(mapFallbackFromApi(list));
      })
      .catch((error) => {
        console.error('Failed to load companies for global scope:', error);
        if (!cancelled) setAllCompaniesFallback([]);
      });

    return () => {
      cancelled = true;
    };
  }, [hasGlobalRole, effectiveCompanies.length]);

  const scope = useMemo(
    () =>
      resolveModuleAccessibleCompanies({
        moduleKey,
        companies: effectiveCompanies,
        globalRoles,
        selectedCompany,
        hasGlobalRole: hasGlobalRoleFlag,
        allCompaniesFallback,
      }),
    [moduleKey, effectiveCompanies, globalRoles, selectedCompany, hasGlobalRoleFlag, allCompaniesFallback]
  );

  const companyOptions = useMemo(
    () =>
      scope.companies
        .map((c) => ({
          id: c.company_id,
          name: c.company_name,
        }))
        .filter((c) => c.id && c.id !== 'global'),
    [scope.companies]
  );

  return {
    ...scope,
    companyOptions,
  };
}
